import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ArrayFieldTemplateItemType, FormContextType, RJSFSchema, StrictRJSFSchema } from '@rjsf/utils';
import { CSSProperties, cloneElement, isValidElement, useEffect, useRef, useState } from 'react';

/** The `ArrayFieldItemTemplate` component is the template used to render an items of an array.
 *
 * @param props - The `ArrayFieldTemplateItemType` props for the component
 */
export default function CustomArrayFieldItemTemplate<
  T = any,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends FormContextType = any
>(props: ArrayFieldTemplateItemType<T, S, F>) {
  const {
    children,
    disabled,
    hasToolbar,
    hasCopy,
    hasMoveDown,
    hasMoveUp,
    hasRemove,
    index,
    onCopyIndexClick,
    onDropIndexClick,
    onReorderClick,
    readonly,
    uiSchema,
    registry,
  } = props;
  const { CopyButton, MoveDownButton, MoveUpButton } = registry.templates.ButtonTemplates;
  const actionButtonStyle: CSSProperties = {
    flex: '0 0 auto',
    width: 32,
    height: 32,
    minHeight: 32,
    minWidth: 32,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    border: '1px solid #cbd4e1',
    backgroundColor: '#f8fafc',
    fontWeight: 600,
    marginLeft: 6,
    lineHeight: 1,
  };
  const removeButtonStyle: CSSProperties = {
    ...actionButtonStyle,
    border: '1px solid #f3c2be',
    backgroundColor: '#fff7f7',
    color: '#b42318',
  };

  const itemTitle = props.children.props.title;
  const titleField = uiSchema?.["ui:titleField"];
  const childProps = (children as any)?.props;
  const currentItemValue = childProps?.formData;
  const titleFieldKey = String(titleField || "").trim();
  const isPrimitiveItem = currentItemValue === null || currentItemValue === undefined || typeof currentItemValue !== 'object';
  const inferredDefaultTitleFieldKey = isPrimitiveItem
    ? "key"
    : (currentItemValue?.["<map>key"] !== undefined ? "<map>key" : "key");
  const resolvedTitleFieldKey = titleFieldKey || inferredDefaultTitleFieldKey;
  const isPrimitiveListTitleAlias = ["key", "value", "<list>key", "<list>value", "<map>key", "<map>value"].includes(resolvedTitleFieldKey);
  const dynamicTitle = (isPrimitiveItem && isPrimitiveListTitleAlias)
    ? currentItemValue
    : currentItemValue?.[resolvedTitleFieldKey] ??
      ((resolvedTitleFieldKey === "key" || resolvedTitleFieldKey === "<map>key") ? currentItemValue?.["<map>key"] : undefined) ??
      ((resolvedTitleFieldKey === "value" || resolvedTitleFieldKey === "<map>value") ? currentItemValue?.["<map>value"] : undefined);
  const dynamicTitleText = dynamicTitle !== undefined && dynamicTitle !== null && String(dynamicTitle).trim() !== ''
    ? String(dynamicTitle)
    : '';
  const [displayedDynamicTitleText, setDisplayedDynamicTitleText] = useState(dynamicTitleText);
  const lastTitleUpdateAtRef = useRef(0);

  useEffect(() => {
    if (displayedDynamicTitleText === dynamicTitleText) {
      return;
    }

    let rafId: number | null = null;
    let timerId: ReturnType<typeof setTimeout> | null = null;

    const applyTitleUpdate = () => {
      rafId = requestAnimationFrame(() => {
        lastTitleUpdateAtRef.current = Date.now();
        setDisplayedDynamicTitleText(dynamicTitleText);
      });
    };

    const elapsedMs = Date.now() - lastTitleUpdateAtRef.current;
    const throttleWindowMs = 80;
    const remainingDelayMs = Math.max(0, throttleWindowMs - elapsedMs);

    if (remainingDelayMs === 0) {
      applyTitleUpdate();
    } else {
      timerId = setTimeout(applyTitleUpdate, remainingDelayMs);
    }

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [dynamicTitleText, displayedDynamicTitleText]);

  const itemIndexLabel = displayedDynamicTitleText
    ? `${displayedDynamicTitleText}`
    : `Entry ${index + 1}`;
  const itemTooltip = `${itemTitle} ${index + 1}${displayedDynamicTitleText ? ` - ${displayedDynamicTitleText}` : ''}`;
  const hasNestedProperties = Array.isArray(childProps?.properties);
  const hasItemDescription = Boolean(
    (typeof childProps?.description === 'string' && childProps.description.trim().length > 0) ||
    (childProps?.description && typeof childProps.description !== 'string')
  );
  const isRedundantAutoTitle =
    typeof itemTitle === 'string' &&
    itemTitle.trim().toLowerCase().endsWith(`-${index + 1}`) &&
    !dynamicTitleText &&
    !hasItemDescription;
  const shouldSuppressChildTitle = hasNestedProperties || isRedundantAutoTitle;
  const childrenWithSmartTitle = isValidElement(children) && shouldSuppressChildTitle
    ? cloneElement(children as any, { title: '' })
    : children;

  return (
    <Accordion
      key={"sub-panel" + index}
      defaultExpanded={index == 0}
      sx={{
        mb: 0,
        border: '1px solid #d4dde8',
        borderRadius: '10px !important',
        overflow: 'hidden',
        '&.Mui-expanded': {
          margin: 0,
          marginBottom: 0
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        id={"sub-panel" + index + "-header"}
        sx={{
          minHeight: 46,
          '&.Mui-expanded': {
            minHeight: 46
          },
          '& .MuiAccordionSummary-content': {
            my: 0.7
          },
          '& .MuiAccordionSummary-content.Mui-expanded': {
            my: 0.7
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            ml: 1.35
          }
        }}
      >

        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap title={itemTooltip} sx={{ flex: 1, minWidth: 0 }}>
            {itemIndexLabel}
          </Typography>
          {hasToolbar && (
            <Box
              sx={{ display: 'inline-flex', alignItems: 'center', mr: 1.2 }}
              onClick={(event) => event.stopPropagation()}
              onFocusCapture={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
                {(hasMoveUp || hasMoveDown) && (
                  <Tooltip title="Move up">
                    <span>
                      <MoveUpButton
                        style={actionButtonStyle}
                        disabled={disabled || readonly || !hasMoveUp}
                        onClick={onReorderClick(index, index - 1)}
                        uiSchema={uiSchema}
                        registry={registry}
                      />
                    </span>
                  </Tooltip>
                )}
                {(hasMoveUp || hasMoveDown) && (
                  <Tooltip title="Move down">
                    <span>
                      <MoveDownButton
                        style={actionButtonStyle}
                        disabled={disabled || readonly || !hasMoveDown}
                        onClick={onReorderClick(index, index + 1)}
                        uiSchema={uiSchema}
                        registry={registry}
                      />
                    </span>
                  </Tooltip>
                )}
                {hasCopy && (
                  <Tooltip title="Duplicate">
                    <span>
                      <CopyButton
                        style={actionButtonStyle}
                        disabled={disabled || readonly}
                        onClick={onCopyIndexClick(index)}
                        uiSchema={uiSchema}
                        registry={registry}
                      />
                    </span>
                  </Tooltip>
                )}
                {hasRemove && (
                  <Tooltip title="Remove">
                    <span>
                      <IconButton
                        size="small"
                        style={removeButtonStyle}
                        disabled={disabled || readonly}
                        onClick={onDropIndexClick(index)}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18, opacity: 0.72 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
            </Box>
          )}
        </Stack>

      </AccordionSummary>
      <AccordionDetails
        sx={{
          pt: 0.6,
          pb: 1,
          px: { xs: 1.1, sm: 1.6, md: 2.2 },
          '& > .form-group.field.field-object': {
            marginBottom: 0
          },
          '& .form-group:last-child': {
            marginBottom: '8px !important'
          },
          '& .form-group.field.field-object .form-group:last-child': {
            marginBottom: '8px !important'
          },
          '& .MuiGrid-root.MuiGrid-item': {
            paddingBottom: 6
          }
        }}
      >
        {childrenWithSmartTitle}
      </AccordionDetails >
    </Accordion >
  );
}